import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import SlideLogo from '../SlideLogo';
import { appTheme } from '../../styles/theme';
import { formatImageUploadError } from '../../utils/formatImageUploadError';
import { readLogoUpload } from '../../utils/logoUpload';

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Preview = styled.div`
  width: 100%;
  height: 56px;
  padding: 8px 10px;
  border: 1px dashed ${appTheme.border};
  border-radius: ${appTheme.radiusSm};
  background: ${appTheme.surfaceHover};
`;

const Hint = styled.p`
  margin: 0;
  font-size: 0.8125rem;
  line-height: 1.45;
  color: ${appTheme.textMuted};
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const ActionButton = styled.button<{ $danger?: boolean }>`
  padding: 8px 14px;
  border-radius: ${appTheme.radiusSm};
  border: 1px solid
    ${({ $danger }) => ($danger ? appTheme.dangerBorder : appTheme.border)};
  background: ${({ $danger }) => ($danger ? appTheme.dangerBg : appTheme.surface)};
  color: ${({ $danger }) => ($danger ? appTheme.dangerText : appTheme.text)};
  font-family: inherit;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: ${({ $danger }) => ($danger ? appTheme.dangerBg : appTheme.surfaceHover)};
  }

  &:focus-visible {
    outline: 2px solid ${appTheme.green};
    outline-offset: 2px;
  }
`;

const HiddenInput = styled.input`
  display: none;
`;

interface DeckLogoPanelProps {
  logoUrl: string | null;
  onLogoChange: (logoUrl: string | null) => void;
}

const DeckLogoPanel: React.FC<DeckLogoPanelProps> = ({ logoUrl, onLogoChange }) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) {
      return;
    }

    try {
      const dataUrl = await readLogoUpload(file);
      onLogoChange(dataUrl);
    } catch (error) {
      window.alert(formatImageUploadError(error, t));
    }
  };

  return (
    <Panel>
      <Preview aria-label={t('editor.logo.previewAria')}>
        <SlideLogo logoUrl={logoUrl} />
      </Preview>
      <Hint>{t('editor.logo.hint')}</Hint>
      <Actions>
        <ActionButton type="button" onClick={() => inputRef.current?.click()}>
          {logoUrl ? t('editor.logo.replace') : t('editor.logo.upload')}
        </ActionButton>
        {logoUrl && (
          <ActionButton type="button" $danger onClick={() => onLogoChange(null)}>
            {t('editor.logo.remove')}
          </ActionButton>
        )}
      </Actions>
      <HiddenInput
        ref={inputRef}
        type="file"
        accept=".svg,.png,.jpg,.jpeg,image/svg+xml,image/png,image/jpeg"
        onChange={handleFile}
      />
    </Panel>
  );
};

export default DeckLogoPanel;
